import jsPDF from "jspdf";
import { sanitizeFilename } from "./pdf";

const formatDateTimeIt = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
    } catch { return iso; }
};

/**
 * Generates the client-side "Ricevuta di consegna" PDF for a single download event.
 *
 * @param {Object} params
 * @param {Object} params.download - one row from /api/delivery/documents/{id}/downloads
 * @param {Object} params.employee - {cognome, nome, matricola, codice_fiscale}
 * @param {Object} params.document - {filename, title, category, period}
 * @param {Object} params.company - {ragione_sociale, sede, p_iva, tagline}
 * @param {string|null} params.firmaDatoreUrl - data:image base64 of employer signature
 * @param {string|null} params.logoUrl - data:image base64 of company logo
 */
export const generateDeliveryReceiptPDF = async ({
    download,
    employee,
    document,
    company,
    firmaDatoreUrl = null,
    logoUrl = null,
}) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = 210;
    const margin = 18;
    let y = 18;

    // Header: logo + company name
    if (logoUrl) {
        try {
            doc.addImage(logoUrl, "PNG", margin, y, 22, 22);
        } catch { /* ignore */ }
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(29, 111, 184); // #1D6FB8
    doc.text(company?.ragione_sociale || "Splendor Sud S.r.l.", margin + 28, y + 6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    if (company?.tagline) doc.text(company.tagline, margin + 28, y + 11);
    if (company?.sede) doc.text(company.sede, margin + 28, y + 15);
    if (company?.p_iva) doc.text(`P.IVA ${company.p_iva}`, margin + 28, y + 19);

    y += 30;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageW - margin, y);

    // Title
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(29, 111, 184);
    doc.text("RICEVUTA DI CONSEGNA DOCUMENTO", pageW / 2, y, { align: "center" });
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text("ai sensi dell'art. 20 del D.Lgs. 82/2005 (CAD) e del Regolamento UE 910/2014", pageW / 2, y, { align: "center" });

    // Employee block
    y += 12;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    doc.text("DESTINATARIO", margin, y);
    y += 2;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, pageW - margin, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Cognome e Nome: ${(employee?.cognome || "").toUpperCase()} ${(employee?.nome || "").toUpperCase()}`, margin, y);
    y += 6;
    doc.text(`Matricola: ${employee?.matricola || "—"}`, margin, y);
    if (employee?.codice_fiscale) {
        doc.text(`Codice Fiscale: ${employee.codice_fiscale}`, margin + 90, y);
    }

    // Document block
    y += 12;
    doc.setFont("helvetica", "bold");
    doc.text("DOCUMENTO CONSEGNATO", margin, y);
    y += 2;
    doc.line(margin, y, pageW - margin, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    const filenameLines = doc.splitTextToSize(`Nome file: ${document?.filename || "—"}`, pageW - 2 * margin);
    doc.text(filenameLines, margin, y);
    y += filenameLines.length * 5 + 2;
    if (document?.title) {
        doc.text(`Titolo: ${document.title}`, margin, y);
        y += 6;
    }
    if (document?.category) {
        doc.text(`Categoria: ${document.category}`, margin, y);
        y += 6;
    }
    if (document?.period) {
        doc.text(`Periodo di riferimento: ${document.period}`, margin, y);
        y += 6;
    }

    // Delivery proof block
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.text("PROVA DI CONSEGNA", margin, y);
    y += 2;
    doc.line(margin, y, pageW - margin, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.text(`Data e ora download: ${formatDateTimeIt(download?.downloaded_at)}`, margin, y);
    y += 6;
    doc.text(`Indirizzo IP: ${download?.ip || "—"}`, margin, y);
    y += 6;

    // SHA-256 hash — break in two lines to fit
    doc.setFontSize(9);
    const sha = download?.file_sha256 || "—";
    doc.text("Impronta digitale (SHA-256):", margin, y);
    y += 5;
    doc.setFont("courier", "normal");
    const sha1 = sha.slice(0, 32);
    const sha2 = sha.slice(32);
    doc.text(sha1, margin, y);
    y += 4;
    doc.text(sha2, margin, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    y += 8;

    // Acknowledgment status
    if (download?.acknowledged) {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(20, 110, 60);
        doc.text("✓ PRESA VISIONE CONFERMATA DAL DIPENDENTE", margin, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont("helvetica", "normal");
        y += 5;
        if (download?.ack_text) {
            doc.setFontSize(9);
            const ackLines = doc.splitTextToSize(`"${download.ack_text}"`, pageW - 2 * margin);
            doc.text(ackLines, margin, y);
            y += ackLines.length * 4 + 2;
            doc.setFontSize(10);
        }
    } else {
        doc.setTextColor(120, 60, 0);
        doc.text("Download semplice (presa visione non richiesta o non confermata)", margin, y);
        doc.setTextColor(0, 0, 0);
        y += 6;
    }

    // Legal disclaimer
    y += 8;
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const disclaimer = "La presente ricevuta è generata automaticamente dal portale HR di " +
        (company?.ragione_sociale || "Splendor Sud S.r.l.") +
        ". I dati di seguito riportati (data, ora, indirizzo IP e impronta SHA-256 del file) costituiscono prova " +
        "informatica della consegna telematica del documento ai sensi dell'art. 20 del D.Lgs. 82/2005 (Codice " +
        "dell'Amministrazione Digitale) e del Regolamento UE 910/2014 (eIDAS).";
    const disclaimerLines = doc.splitTextToSize(disclaimer, pageW - 2 * margin);
    doc.text(disclaimerLines, margin, y);
    y += disclaimerLines.length * 3.5 + 8;

    // Signature
    if (firmaDatoreUrl) {
        try {
            const sigW = 50;
            const sigH = 22;
            const sigX = pageW - margin - sigW;
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.text("Il Datore di Lavoro", sigX, y);
            y += 2;
            doc.addImage(firmaDatoreUrl, "PNG", sigX, y, sigW, sigH);
            y += sigH + 2;
            doc.line(sigX, y, sigX + sigW, y);
        } catch { /* ignore */ }
    }

    // Footer
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text(
        `Documento generato il ${formatDateTimeIt(new Date().toISOString())} · Ricevuta n. ${(download?.id || "").slice(0, 8)}`,
        pageW / 2, 287, { align: "center" }
    );

    const filename = `ricevuta-consegna-${sanitizeFilename((employee?.cognome || "") + "-" + (employee?.nome || ""))}-${sanitizeFilename(document?.filename || "documento")}-${(download?.downloaded_at || "").slice(0, 10)}.pdf`;
    doc.save(filename);
};
